import { Suspense, useDeferredValue } from 'react'
import { createFileRoute, Link, stripSearchParams, useMatch } from '@tanstack/react-router'
import { useSuspenseInfiniteQuery } from '@tanstack/react-query'
import * as valibot from 'valibot'
import { TRACE_SINCE, type TraceSince } from 'shared/wide-event-logs'
import { MonitorSmartphoneIcon, MousePointerBanIcon, MousePointerClickIcon, ZapIcon } from 'lucide-react'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { SearchInput } from '@/components/ui/search-input'
import { useDebouncedSearch } from '@/hooks/use-debounced-search'
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll'
import { cn } from '@/lib/utils'
import { SETTINGS_STACK, SettingsSection } from './-components/settings/settings-section'
import { AppWindowIcon } from './-components/settings/settings-icons'
import { ADMIN_TONE } from './-components/admin/admin-pages'
import { SuperAdminBoundary } from './-components/admin/super-admin-boundary'
import { formatUtc, StoreDownNotice } from './-components/admin/traces-shared'
import { SESSIONS_DESCRIPTION, SessionsListSkeleton, SessionsPending } from './-components/admin/sessions-pending'
import { formatDurationHuman } from './-components/admin/session-timeline'
import type { SessionSummary } from './-components/admin/session-player-math'
import { deviceLine, LiveBadge, PersonAvatar, personLabel, RelativeTime } from './-components/admin/session-player'

// Every browser tab's journey, the most recently active first: who, from which page, for how long, and what broke.
// A row opens the session's time-aligned timeline.

const SEARCH_DEFAULTS = { since: '24h' as const, errors: false, q: '' }

export const Route = createFileRoute('/_app/admin/sessions')({
  validateSearch: valibot.object({
    since: valibot.fallback(valibot.optional(valibot.picklist(TRACE_SINCE), '24h'), '24h'),
    errors: valibot.fallback(valibot.optional(valibot.boolean(), false), false),
    q: valibot.fallback(valibot.optional(valibot.string(), ''), ''),
  }),
  search: { middlewares: [stripSearchParams(SEARCH_DEFAULTS)] },
  component: RouteComponent,
  pendingComponent: SessionsPending,
  staticData: { fullWidth: true },
})

function RouteComponent() {
  return (
    <div className={SETTINGS_STACK}>
      <SettingsSection icon={AppWindowIcon} tone={ADMIN_TONE} title="Sessions" description={SESSIONS_DESCRIPTION}>
        <SuperAdminBoundary what="sessions">
          <div className="flex flex-col gap-4">
            <SessionFilterBar />
            <Suspense fallback={<SessionsListSkeleton />}>
              <SessionsList />
            </Suspense>
          </div>
        </SuperAdminBoundary>
      </SettingsSection>
    </div>
  )
}

// MARK: Filters

const SINCE_LABEL: Record<TraceSince, string> = {
  '1h': 'Active in the last hour',
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
}

function SessionFilterBar() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const { searchInput, handleSearchChange } = useDebouncedSearch({ initialValue: search.q })
  return (
    <div className="flex flex-wrap items-center gap-3">
      <NativeSelect
        size="sm"
        aria-label="Show"
        value={search.errors ? 'errors' : 'all'}
        onChange={(event) =>
          void navigate({ search: (prev) => ({ ...prev, errors: event.target.value === 'errors' }), replace: true })
        }
      >
        <NativeSelectOption value="all">All sessions</NativeSelectOption>
        <NativeSelectOption value="errors">With errors</NativeSelectOption>
      </NativeSelect>
      <NativeSelect
        size="sm"
        aria-label="Range"
        value={search.since}
        onChange={(event) =>
          void navigate({
            search: (prev) => ({ ...prev, since: event.target.value as TraceSince }),
            replace: true,
          })
        }
      >
        {TRACE_SINCE.map((since) => (
          <NativeSelectOption key={since} value={since}>
            {SINCE_LABEL[since]}
          </NativeSelectOption>
        ))}
      </NativeSelect>
      <div className="max-w-xs flex-1">
        <SearchInput
          value={searchInput}
          onChange={handleSearchChange}
          placeholder="Page, procedure, control or session id…"
          aria-label="Search sessions"
          className="h-9"
        />
      </div>
    </div>
  )
}

// MARK: List

const ROW_GRID = 'lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(0,1.8fr)_5.5rem_5rem_repeat(3,4rem)_minmax(0,1.7fr)]'

function SessionsList() {
  const context = useMatch({ from: '/_app/admin/sessions/_list' }).context
  const deferred = useDeferredValue(context.sessionsInfiniteQueryOptions)
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } = useSuspenseInfiniteQuery(deferred)
  const { loadMoreRef } = useInfiniteScroll({ hasNextPage, isFetchingNextPage, fetchNextPage })
  const stale = isFetching || deferred !== context.sessionsInfiniteQueryOptions

  const firstPage = data.pages[0]
  if (firstPage && !firstPage.ok) return <StoreDownNotice reason={firstPage.reason} />
  const sessions = data.pages.flatMap((page) => (page.ok ? page.sessions : []))
  if (sessions.length === 0)
    return (
      <p className="text-muted-foreground rounded-xl border p-6 text-center text-sm">
        No sessions match these filters.
      </p>
    )

  return (
    <div
      role="list"
      aria-label="Sessions"
      className={cn(
        'flex flex-col overflow-hidden rounded-xl border transition-opacity',
        stale && !isFetchingNextPage && 'opacity-60',
      )}
    >
      <div
        aria-hidden
        className={cn(
          'text-muted-foreground bg-muted/40 hidden gap-3 border-b px-4 py-2 text-[11px] tracking-wide uppercase',
          ROW_GRID,
        )}
      >
        <span>User</span>
        <span>Journey</span>
        <span>Started</span>
        <span className="text-right">Duration</span>
        <span className="text-right">Pages</span>
        <span className="text-right">Actions</span>
        <span className="text-right">Errors</span>
        <span>Device</span>
      </div>
      {sessions.map((session) => (
        <SessionRow key={session.sessionId} session={session} />
      ))}
      {hasNextPage && <div ref={loadMoreRef} className="h-8" />}
    </div>
  )
}

function SessionRow(props: { session: SessionSummary }) {
  const session = props.session
  return (
    <Link
      role="listitem"
      to="/admin/sessions/$sessionId"
      params={{ sessionId: session.sessionId }}
      search={{ at: session.lastSeenAt }}
      aria-label={`Session of ${personLabel(session.user)}, ${session.entryPath ?? 'unknown page'}`}
      data-telemetry-label="open-session"
      className={cn(
        'hover:bg-muted/50 flex flex-col gap-2 border-b px-4 py-3 text-sm last:border-b-0 lg:items-center lg:gap-3',
        ROW_GRID,
      )}
    >
      <span className="flex min-w-0 items-center gap-3">
        <PersonAvatar person={session.user} />
        <span className="flex min-w-0 flex-col">
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate font-medium">{personLabel(session.user)}</span>
            {session.live && <LiveBadge />}
          </span>
          <span className="text-muted-foreground truncate text-xs">
            {session.company?.name ?? session.user?.email ?? (
              <span className="font-mono">{session.sessionId.slice(0, 8)}</span>
            )}
          </span>
        </span>
      </span>
      <span className="flex min-w-0 items-center gap-1.5 font-mono text-xs">
        <span className="truncate" title={session.entryPath ?? undefined}>
          {session.entryPath ?? '—'}
        </span>
        {session.exitPath && session.exitPath !== session.entryPath && (
          <>
            <span className="text-muted-foreground shrink-0">→</span>
            <span className="text-muted-foreground truncate" title={session.exitPath}>
              {session.exitPath}
            </span>
          </>
        )}
      </span>
      <span className="text-muted-foreground text-xs tabular-nums" title={formatUtc(session.startedAt)}>
        <RelativeTime iso={session.startedAt} />
      </span>
      <span className="flex flex-wrap items-center gap-3 text-xs tabular-nums lg:contents">
        <span className="lg:text-right">{formatDurationHuman(session.durationMs)}</span>
        <span className="lg:text-right">
          {session.pageCount}
          <span className="text-muted-foreground lg:hidden"> pages</span>
        </span>
        <span className="flex items-center gap-1 lg:justify-end">
          <MousePointerClickIcon className="text-muted-foreground size-3" aria-hidden />
          {session.interactionCount}
          {session.rageClickCount > 0 && (
            <span className="text-amber-600 dark:text-amber-400" title="Rage clicks">
              <ZapIcon className="inline size-3" aria-label="Rage clicks" />
              {session.rageClickCount}
            </span>
          )}
          {session.deadClickCount > 0 && (
            <span className="text-muted-foreground" title="Dead clicks">
              <MousePointerBanIcon className="inline size-3" aria-label="Dead clicks" />
              {session.deadClickCount}
            </span>
          )}
        </span>
        <span
          className={cn(
            'lg:text-right',
            session.errorCount > 0 ? 'text-destructive font-medium' : 'text-muted-foreground',
          )}
        >
          {session.errorCount}
          <span className="lg:hidden"> errors</span>
        </span>
      </span>
      <span className="text-muted-foreground flex min-w-0 items-center gap-1.5 text-xs">
        <MonitorSmartphoneIcon className="size-3.5 shrink-0" aria-hidden />
        <span className="truncate">{deviceLine(session.device)}</span>
      </span>
    </Link>
  )
}
